 

import React, { useState, useMemo, useEffect } from "react";
import { PlusCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import ExcelExport from "../../UserControls/ExcelExport/ExcelExport";
import logo from "../../images/SPFLLOGO.png";

export default function AssetMovementList() {
  const [filter, setFilter] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [movementList, setMovementList] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const companyName = sessionStorage.getItem("companyName");

  // ✅ Fetch asset movement list
  const fetchMovementList = async () => {
    try {
      setLoading(true);
      const userName = sessionStorage.getItem("userName") || "";

      const response = await api.GetAssetMovementList(userName);
      console.log("📦 API Response:", response);

      if (response && response.Table && Array.isArray(response.Table)) {
        setMovementList(response.Table);
      } else {
        setMovementList([]);
      }
    } catch (error) {
      console.error("❌ Error fetching movement list:", error);
      setMovementList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovementList();
  }, []);

  // ✅ Filtered data for search
  const filteredData = useMemo(() => {
    if (!filter) return movementList;
    return movementList.filter((m) =>
      Object.values(m)
        .join(" ")
        .toLowerCase()
        .includes(filter.toLowerCase())
    );
  }, [filter, movementList]);
 
   
  // ✅ Handle Edit button click
 const handleEdit = async (MovementID) => {
   try {
     // fetch the edit record
     const result = await api.EditAssetMovement(MovementID);
     console.log("✏️ Edit Data (raw):", result);

     // be tolerant of different response shapes:
     const editRow =
       result?.Table?.[0] ||
       result?.EditAssetMovementAPIjs?.Table?.[0] ||
       result?.data?.Table?.[0] ||
       (Array.isArray(result) ? result[0] : null) ||
       result;

     if (!editRow) {
       console.warn("⚠️ Could not find edit row in API response", result);
       // still navigate so user can see blank form, or optionally show an alert
       navigate(`/movement/edit/${MovementID}`);
       return;
     }

     // pass edit data via location.state so the edit page can prefill
     navigate(`/movement/edit/${MovementID}`, { state: { editData: editRow } });
   } catch (error) {
     console.error("❌ Edit fetch failed:", error);
   }
 };

  // ✅ Handle Print button click
const handlePrint = async (GipId) => {
  try {
    if (!GipId) {
      alert("⚠️ Missing GIP ID — cannot print this record.");
      console.warn("Invalid GipId:", GipId);
      return;
    }

    console.log("🖨️ Printing for GipId:", GipId);
    const result = await api.GetDCNonRetHTMLPrint(GipId);
    const printData = result?.GetDCNonRetHTMLPrintjs || result;

    if (!printData) {
      alert("No print data returned from API.");
      return;
    }

    const response = await fetch("/Dc_Non_Returnable.htm");
    let htmlContent = await response.text();
console.group("🧩 PRINT DEBUG");
console.log("📄 HTML length:", htmlContent.length);
console.log("📄 HTML snippet start:", htmlContent.slice(0, 300));
console.log("📄 HTML contains 'RepeatSection'? ->", htmlContent.includes("RepeatSection"));
console.log("📄 HTML snippet around RepeatSection:");
const repeatIndex = htmlContent.indexOf("RepeatSection");
if (repeatIndex !== -1) {
  console.log(htmlContent.substring(Math.max(0, repeatIndex - 50), repeatIndex + 100));
}

    htmlContent = populatePrintTemplate(htmlContent, printData);

    const printWindow = window.open("", "_blank", "width=900,height=700,scrollbars=yes,resizable=yes");
    if (!printWindow) return alert("Please enable popups to print.");

    printWindow.document.write(htmlContent);
    printWindow.document.write(`
  <style>
    body, td, th {
      color: black !important;
      font-family: Arial, sans-serif !important;
      font-size: 10pt !important;
    }
  </style>
`);
    printWindow.document.close();
    printWindow.focus();
  } catch (error) {
    console.error("❌ Print fetch failed:", error);
    alert("Failed to load print data. Please try again.");
  }
};

// ✅ Function to populate HTML template with API data
const populatePrintTemplate = (htmlContent, printData) => {
  try {
    console.group("🧩 PRINT DEBUG - populatePrintTemplate");
    console.log("🔄 Starting template population...");
    console.log("🗂 printData keys:", Object.keys(printData || {}));

    if (!printData) {
      console.warn("⚠️ printData is falsy");
    }

    // 🔹 Clean BOM, normalize newlines & NBSP
    htmlContent = (htmlContent || "")
      .replace(/\uFEFF/g, "")
      .replace(/\r\n/g, "\n")
      .replace(/\u00A0/g, " ");

    console.log("📄 HTML length after cleanup:", htmlContent.length);

    // 🔹 Map single-row data sources
    const dataMap = {
      C: printData?.Table2?.[0] || {},
      V: printData?.Table3?.[0] || {},
      GNRH: printData?.Table4?.[0] || {},
    };

    console.log("📌 Mapped single-row tables:", {
      C: Object.keys(dataMap.C || {}).slice(0, 8),
      V: Object.keys(dataMap.V || {}).slice(0, 8),
      GNRH: Object.keys(dataMap.GNRH || {}).slice(0, 8),
    });

    // 🔹 Safe, case-insensitive getter
    const getValue = (obj, key) => {
      if (!obj || !key) return "";
      const cleanKey = String(key)
        .replace(/[\u00A0\r\n\t ]+/g, "")
        .trim()
        .toLowerCase();
      const matchKey = Object.keys(obj).find(
        (k) => k.toLowerCase() === cleanKey
      );
      return matchKey ? (obj[matchKey] ?? "") : "";
    };

    // 🔹 Replace non-repeating placeholders
    htmlContent = htmlContent.replace(
      /<!--\s*\{([A-Z0-9_]+)\}\s*\.\s*\[\s*([^\]]+?)\s*\]\s*-->/gi,
      (m, tag, field) => {
        const val = getValue(dataMap[tag], field);
        console.debug(`🔸 ${tag}.${field} -> "${val}"`);
        return val ?? "";
      }
    );

    // 🔹 Replace {@InvoiceCopyName}
    const copyList = printData?.Table || [];
    const copyObj =
      (Array.isArray(copyList) &&
        (copyList.find((x) => x.Default === 1) || copyList[0])) ||
      {};
    const copyName = copyObj?.CopyName || "Original";
    htmlContent = htmlContent.replace(/{@InvoiceCopyName}/g, copyName);
    console.log("📑 Invoice copy name used:", copyName);

    // 🔹 Find RepeatSection - try multiple patterns
    const patterns = [
      /<\s*RepeatSection[^>]*>([\s\S]*?)<\/\s*RepeatSection\s*>/i,
      /<RepeatSection>([\s\S]*?)<\/RepeatSection>/i,
      /RepeatSection>([\s\S]*?)<\/RepeatSection/i,
      /RepeatSection([\s\S]*?)<\/RepeatSection/i,
    ];

    let match = null;
    let usedPattern = null;

    for (const pattern of patterns) {
      match = htmlContent.match(pattern);
      if (match) {
        usedPattern = pattern;
        break;
      }
    }

    console.log("🔍 RepeatSection search results:");
    console.log("🔍 Pattern tried:", patterns.map((p) => p.toString()));
    console.log("🔍 Match found?", !!match);
    console.log("🔍 Used pattern:", usedPattern?.toString());
    console.log("🔍 Match details:", match);

    if (match) {
      let rowTemplate = match[1].replace(/\u00A0/g, " ");
      const items = (printData?.Table5 || []).filter((r) => {
        const p = r?.Partno || r?.PARTNO || r?.partNo;
        return p && String(p).trim() !== "";
      });

      console.log("📦 Filtered valid items:", items.length);

  let rowsHtml = "";
const totalRows = 10; // ✅ Minimum total rows per page (including empty)

// ✅ Render actual data rows
items.forEach((item, idx) => {
  const slNo = item?.SlNo || idx + 1;
  const partNo = item?.Partno || "";
  const desc = item?.PartDesc || "";
  const hsn = item?.HSNCode || "";
  const uom = item?.UOM || "";
  const qty = item?.Qty || "";
  const rate = item?.Rate || "";
  const amount = item?.Amount || "";

  rowsHtml += `
    <tr height=20 style='mso-height-source:userset;height:15.0pt'>
      <td height=20 class="style4"
        style='border-top-style:none;border-top-color:inherit;border-top-width:medium;'>${slNo}</td>
      <td colspan=3 class="xl781326"
        style='border-right:.5pt solid black;border-left:none;width:100pt;'>${partNo}</td>
      <td colspan=7 class="xl781326"
        style='border-right:.5pt solid black;border-left:none;width:134pt;'>${desc}</td>
      <td colspan=2 class="xl1051326"
        style='border-right:.5pt solid black;border-left:none;width:50pt;'>${hsn}</td>
      <td class="style5" style='border-left:none;'>${uom}</td>
      <td colspan=3 class="xl1561326"
        style='border-right:.5pt solid black;border-left:none;'>${qty}</td>
      <td colspan=2 class="xl1561326"
        style='border-right:.5pt solid black;border-left:none;'>${rate}</td>
      <td colspan=2 class="style6"
        style='border-right:.5pt solid black;border-left-style:none;border-left-color:inherit;border-left-width:medium;'>${amount}</td>
    </tr>
  `;
});

// ✅ Add empty rows if less than 10
if (items.length < totalRows) {
  const emptyCount = totalRows - items.length;
  for (let i = 0; i < emptyCount; i++) {
    rowsHtml += `
      <tr height=20 style='mso-height-source:userset;height:15.0pt'>
        <td height=20 class="style4"
          style='border-top-style:none;border-top-color:inherit;border-top-width:medium;'>&nbsp;</td>
        <td colspan=3 class="xl781326"
          style='border-right:.5pt solid black;border-left:none;width:100pt;'>&nbsp;</td>
        <td colspan=7 class="xl781326"
          style='border-right:.5pt solid black;border-left:none;width:134pt;'>&nbsp;</td>
        <td colspan=2 class="xl1051326"
          style='border-right:.5pt solid black;border-left:none;width:50pt;'>&nbsp;</td>
        <td class="style5" style='border-left:none;'>&nbsp;</td>
        <td colspan=3 class="xl1561326"
          style='border-right:.5pt solid black;border-left:none;'>&nbsp;</td>
        <td colspan=2 class="xl1561326"
          style='border-right:.5pt solid black;border-left:none;'>&nbsp;</td>
        <td colspan=2 class="style6"
          style='border-right:.5pt solid black;border-left-style:none;border-left-color:inherit;border-left-width:medium;'>&nbsp;</td>
      </tr>
    `;
  }
}
      if (rowsHtml.trim()) {
        htmlContent = htmlContent.replace(usedPattern, rowsHtml);
        console.log("✅ RepeatSection replaced with item rows.");
      } else {
        console.warn("⚠️ No valid rows found — RepeatSection removed.");
        htmlContent = htmlContent.replace(usedPattern, "");
      }
    } else {
      console.warn("⚠️ RepeatSection not found in HTML template!");
    }

    // 🔹 Cleanup any leftover placeholders
    htmlContent = htmlContent.replace(
      /<!--\s*\{[A-Z0-9_]+\}\s*\.\s*\[[^\]]+\]\s*-->/gi,
      ""
    );

    console.log("📄 Final HTML length:", htmlContent.length);
    console.groupEnd();
    return htmlContent;
  } catch (err) {
    console.error("❌ populatePrintTemplate error:", err);
    console.groupEnd();

    // ✅ Force visible text in Excel-exported HTML
    htmlContent = htmlContent.replace(
      /<\/head>/i,
      `<style>
         body, table, tr, td, th, p, span, font {
           color: black !important;
           visibility: visible !important;
           font-family: Arial, sans-serif !important;
           font-size: 10pt !important;
         }
         td {
           mso-number-format: "\\@"; /* prevent Excel-format hiding */
         }
       </style></head>`
    );

    return htmlContent;
  }
};



  return (
    <div className="movement-dashboard">
      <style>{`
        .movement-dashboard {
          background: #f9fafb;
          padding: 24px;
          font-family: "Inter", sans-serif;
          min-height: 100vh;
        }

        .header-section h2 {
          text-align: center;
          font-size: 1.4rem;
          font-weight: 500;
          color: #1f2937;
        }

        // .btn-primary {
        //   display: flex;
        //   align-items: center;
        //   gap: 8px;
        //   background: linear-gradient(90deg, #2563eb, #1d4ed8);
        //   color: white;
        //   border: none;
        //   padding: 10px 18px;
        //   border-radius: 8px;
        //   font-weight: 500;
        //   cursor: pointer;
        //   transition: background 0.3s ease;
        // }
        // .btn-primary:hover {
        //   background: linear-gradient(90deg, #1d4ed8, #1e40af);
        // }

        .btn-primary {
  display: flex;
  align-items: center;
  gap: 4px; /* smaller space between icon and text */
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 5px 10px; /* smaller padding */
  font-size: 13px; /* reduced font size */
  cursor: pointer;
}

.btn-primary:hover {
  background: #1d4ed8;
}


        .btn-edit {
          background: #1b6cc9ff;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          transition: 0.3s ease;
        }
        .btn-edit:hover {
          background: #1b6cc9ff;
        }

        .btn-print {
          background: #10b981;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          transition: 0.3s ease;
        }
        .btn-print:hover {
          background: #059669;
        }

        .controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          margin-top: 16px;
          gap: 10px;
        }

        .controls input {
          flex: 1;
          min-width: 180px;
          padding: 10px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.95rem;
          outline: none;
          transition: 0.2s;
        }

        .controls input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
        }

        .view-toggle {
          display: flex;
          gap: 8px;
        }

        .view-toggle button {
          padding: 8px 16px;
          border-radius: 6px;
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .view-toggle button:hover {
          background: #f3f4f6;
        }

        .view-toggle .active {
          background: #2563eb;
          color: white;
          border-color: #1d4ed8;
        }

        .table-container {
          overflow-x: auto;
          margin-top: 20px;
          background: white;
          border-radius: 10px;
          box-shadow: 0 1px 3px rgba(59, 51, 51, 0.08);
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
        }

        thead {
          background: #383131ff;
          color: white;
        }

        th, td {
          text-align: left;
          padding: 10px 14px;
          border-bottom: 1px solid #e5e7eb;
        }

        tbody tr:hover {
          background: #f9fafb;
        }

        .card-container {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
          margin-top: 20px;
        }

        .movement-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .movement-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
        }

        .movement-card h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1d4ed8;
          margin-bottom: 8px;
        }

        .movement-card p {
          font-size: 0.9rem;
          color: #374151;
          margin: 4px 0;
        }

        .movement-card strong {
          color: #111827;
        }

        .loader {
          text-align: center;
          padding: 40px;
          font-size: 1rem;
          color: #374151;
        }
          /* Styles copied from TicketSummary.js for logo/company name header */
        .header-controls {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 20px;
          background: #fff;
          padding: 4px 20px;
          border-radius: 10px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          position: relative; /* To center the title */
        }
        
        .logo-and-name {
          display: flex;
          align-items: center;
          position: absolute; /* Position absolute to move it to the left */
          left: 20px;
          top: 50%;
          transform: translateY(-50%);
        }
        
        .logo-and-name img {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background-color: white;
          box-shadow: 0 0 4px rgba(0,0,0,0.2);
          padding: 5px;
          margin-right: 1px;
          object-fit: contain;
        }

        .logo-and-name h1 {
          font-size: 22px;
          font-weight: bold;
          color: #333;
          margin: 0;
          text-align: left;
        }
          
        /* -------------------- RESPONSIVE FIXES -------------------- */
@media (max-width: 1024px) {
  .header-controls {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 10px;
  }

  .logo-and-name {
    position: static;
    transform: none;
    justify-content: center;
    margin-bottom: 8px;
  }

  .logo-and-name img {
    width: 50px;
    height: 50px;
  }

  .logo-and-name h1 {
    font-size: 18px;
  }

  .header-section h2 {
    font-size: 1.2rem;
  }

  .controls {
    flex-direction: column;
    align-items: stretch;
  }

  .controls input {
    width: 100%;
  }

  .view-icons {
    display: flex;
    justify-content: center;
    gap: 8px;
    width: 100%;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  table {
    font-size: 0.8rem;
  }

  th, td {
    padding: 8px 10px;
  }

  .table-container {
    border-radius: 6px;
    overflow-x: auto;
  }

  .movement-card {
    padding: 12px;
  }
}

@media (max-width: 768px) {
  .header-section h2 {
    font-size: 1rem;
  }

  .logo-and-name img {
    width: 40px;
    height: 40px;
  }

  .logo-and-name h1 {
    font-size: 16px;
  }

  .btn-primary {
    font-size: 12px;
    padding: 6px 8px;
  }

  .controls {
    flex-direction: column;
    gap: 10px;
  }

  .controls input {
    width: 100%;
    font-size: 0.85rem;
  }

  .movement-card {
    font-size: 0.85rem;
  }

  .movement-card h3 {
    font-size: 1rem;
  }

  .movement-card p {
    font-size: 0.85rem;
  }

  .card-container {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .header-controls {
    padding: 8px;
  }

  .logo-and-name {
    flex-direction: column;
    align-items: center;
  }

  .logo-and-name h1 {
    font-size: 15px;
    text-align: center;
  }

  .btn-edit,
  .btn-print {
    padding: 5px 8px;
    font-size: 0.75rem;
  }

  th,
  td {
    padding: 6px;
    white-space: nowrap;
  }

  table {
    width: 900px; /* allow horizontal scroll */
  }
}

      `}</style>

      <div className="header-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
         <div className="header-controls">
        <div className="logo-and-name">
          {/* <img
            src={logo}
            alt="Company Logo"
          /> */}
          <h1>{companyName}</h1>
        </div>
        <h2 className="page-title">Asset Movement</h2>
        </div>
          <button
            className="btn-primary"
            onClick={() => navigate("/movement/new")}
          >
            <PlusCircle className="w-4 h-4" /> New
          </button>
        </div>

        <div className="controls">
          <input
            type="text"
            placeholder="🔍 Search movement details..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />

 <ExcelExport data={movementList} fileName="AssetMovementList" />
 
          <div className="view-icons">
            <button
              onClick={() => setViewMode("card")}
              className={`icon-btn ${viewMode === "card" ? "active" : ""}`}
              title="Card View"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon-svg"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
              </svg>
            </button>

            <button
              onClick={() => setViewMode("table")}
              className={`icon-btn ${viewMode === "table" ? "active" : ""}`}
              title="Table View"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon-svg"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M4 6h16v2H4V6zm0 4h16v2H4v-2zm0 4h16v2H4v-2zm0 4h16v2H4v-2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loader">Loading movement data...</div>
      ) : filteredData.length === 0 ? (
        <div className="loader">No asset movements found.</div>
      ) : viewMode === "table" ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Doc No</th>
                <th>Asset Name</th>
                <th>Movement Date</th>
                <th>Reason</th>
                <th>FromLocation</th>
                <th>ToLocation</th>
                <th>Returnable</th>
                <th>Expected Return</th>
                <th>Auth Order</th>
                <th>Transport</th>
                <th>Condition</th>
                <th>Print</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((move) => (
                <tr key={move.MovementID}>
                  <td>{move.MovementDocNo}</td>
                  <td>{move.AssetName}</td>
                  <td>{move.MovementDate?.split("T")[0]}</td>
                  <td>{move.MovementReason}</td>
                  <td>{move.FromLocation}</td>
                  <td>{move.ToLocation}</td>
                  <td>{move.IsReturnable ? "Yes" : "No"}</td>
                  <td>{move.ExpectedReturnDate?.split("T")[0]}</td>
                  <td>{move.AuthOrderNo}</td>
                  <td>{move.TransportMode}</td>
                  <td>{move.ConditionNotes}</td>
                  <td>
                    <button
                      className="btn-print"
                     onClick={() => handlePrint(move.GIPNRId || move.GipId || move.GIPID)}
                    >
                      🖨️ Print
                    </button>
                  </td>
                  <td>
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(move.MovementID)}
                    >
                      ✏️ Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-container">
          {filteredData.map((move) => (
            <div key={move.MovementID} className="movement-card">
              <h3>{move.MovementDocNo}</h3>
              <p><strong>Asset:</strong> {move.AssetName}</p>
              <p><strong>Date:</strong> {move.MovementDate?.split("T")[0]}</p>
              <p><strong>Reason:</strong> {move.MovementReason}</p>
              <p><strong>From:</strong> {move.FromLocation}</p>
              <p><strong>To:</strong> {move.ToLocation}</p>
              <p><strong>Transport:</strong> {move.TransportMode}</p>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button
                  className="btn-print"
                 onClick={() => handlePrint(move.GIPNRId || move.GipId || move.GIPID)}
                >
                  🖨️ Print
                </button>
                <button
                  className="btn-edit"
                  onClick={() => handleEdit(move.MovementID)}
                >
                  ✏️ Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}




// import React, { useState, useMemo, useEffect } from "react";
// import { PlusCircle } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import api from "../../api/api";
// import ExcelExport from "../../UserControls/ExcelExport/ExcelExport";
// import logo from "../../images/SPFLLOGO.png";

// export default function AssetMovementList() {
//   const [filter, setFilter] = useState("");
//   const [viewMode, setViewMode] = useState("table");
//   const [movementList, setMovementList] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const navigate = useNavigate();
//   const companyName = sessionStorage.getItem("companyName");

//   // ✅ Fetch asset movement list
//   const fetchMovementList = async () => {
//     try {
//       setLoading(true);
//       const userName = sessionStorage.getItem("userName") || "";

//       const response = await api.GetAssetMovementList(userName);
//       console.log("📦 API Response:", response);

//       if (response && response.Table && Array.isArray(response.Table)) {
//         setMovementList(response.Table);
//       } else {
//         setMovementList([]);
//       }
//     } catch (error) {
//       console.error("❌ Error fetching movement list:", error);
//       setMovementList([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchMovementList();
//   }, []);

//   // ✅ Filtered data for search
//   const filteredData = useMemo(() => {
//     if (!filter) return movementList;
//     return movementList.filter((m) =>
//       Object.values(m)
//         .join(" ")
//         .toLowerCase()
//         .includes(filter.toLowerCase())
//     );
//   }, [filter, movementList]);
 
   
//   // ✅ Handle Edit button click
//  const handleEdit = async (MovementID) => {
//    try {
//      // fetch the edit record
//      const result = await api.EditAssetMovement(MovementID);
//      console.log("✏️ Edit Data (raw):", result);

//      // be tolerant of different response shapes:
//      const editRow =
//        result?.Table?.[0] ||
//        result?.EditAssetMovementAPIjs?.Table?.[0] ||
//        result?.data?.Table?.[0] ||
//        (Array.isArray(result) ? result[0] : null) ||
//        result;

//      if (!editRow) {
//        console.warn("⚠️ Could not find edit row in API response", result);
//        // still navigate so user can see blank form, or optionally show an alert
//        navigate(`/movement/edit/${MovementID}`);
//        return;
//      }

//      // pass edit data via location.state so the edit page can prefill
//      navigate(`/movement/edit/${MovementID}`, { state: { editData: editRow } });
//    } catch (error) {
//      console.error("❌ Edit fetch failed:", error);
//    }
//  };

//   // ✅ Handle Print button click
// const handlePrint = async (GipId) => {
//   try {
//     if (!GipId) {
//       alert("⚠️ Missing GIP ID — cannot print this record.");
//       console.warn("Invalid GipId:", GipId);
//       return;
//     }

//     console.log("🖨️ Printing for GipId:", GipId);
//     const result = await api.GetDCNonRetHTMLPrint(GipId);
//     const printData = result?.GetDCNonRetHTMLPrintjs || result;

//     if (!printData) {
//       alert("No print data returned from API.");
//       return;
//     }

//     const response = await fetch("/Dc_Non_Returnable.htm");
//     let htmlContent = await response.text();
// console.group("🧩 PRINT DEBUG");
// console.log("📄 HTML length:", htmlContent.length);
// console.log("📄 HTML snippet start:", htmlContent.slice(0, 300));
// console.log("📄 HTML contains 'RepeatSection'? ->", htmlContent.includes("RepeatSection"));
// console.log("📄 HTML snippet around RepeatSection:");
// const repeatIndex = htmlContent.indexOf("RepeatSection");
// if (repeatIndex !== -1) {
//   console.log(htmlContent.substring(Math.max(0, repeatIndex - 50), repeatIndex + 100));
// }

//     htmlContent = populatePrintTemplate(htmlContent, printData);

//     const printWindow = window.open("", "_blank", "width=900,height=700,scrollbars=yes,resizable=yes");
//     if (!printWindow) return alert("Please enable popups to print.");

//     printWindow.document.write(htmlContent);
//     printWindow.document.write(`
//   <style>
//     body, td, th {
//       color: black !important;
//       font-family: Arial, sans-serif !important;
//       font-size: 10pt !important;
//     }
//   </style>
// `);
//     printWindow.document.close();
//     printWindow.focus();
//   } catch (error) {
//     console.error("❌ Print fetch failed:", error);
//     alert("Failed to load print data. Please try again.");
//   }
// };

// // ✅ Function to populate HTML template with API data
// const populatePrintTemplate = (htmlContent, printData) => {
//   try {
//     console.group("🧩 PRINT DEBUG - populatePrintTemplate");
//     console.log("🔄 Starting template population...");
//     console.log("🗂 printData keys:", Object.keys(printData || {}));

//     if (!printData) {
//       console.warn("⚠️ printData is falsy");
//     }

//     // 🔹 Clean BOM, normalize newlines & NBSP
//     htmlContent = (htmlContent || "")
//       .replace(/\uFEFF/g, "")
//       .replace(/\r\n/g, "\n")
//       .replace(/\u00A0/g, " ");

//     console.log("📄 HTML length after cleanup:", htmlContent.length);

//     // 🔹 Map single-row data sources
//     const dataMap = {
//       C: printData?.Table2?.[0] || {},
//       V: printData?.Table3?.[0] || {},
//       GNRH: printData?.Table4?.[0] || {},
//     };

//     console.log("📌 Mapped single-row tables:", {
//       C: Object.keys(dataMap.C || {}).slice(0, 8),
//       V: Object.keys(dataMap.V || {}).slice(0, 8),
//       GNRH: Object.keys(dataMap.GNRH || {}).slice(0, 8),
//     });

//     // 🔹 Safe, case-insensitive getter
//     const getValue = (obj, key) => {
//       if (!obj || !key) return "";
//       const cleanKey = String(key)
//         .replace(/[\u00A0\r\n\t ]+/g, "")
//         .trim()
//         .toLowerCase();
//       const matchKey = Object.keys(obj).find(
//         (k) => k.toLowerCase() === cleanKey
//       );
//       return matchKey ? (obj[matchKey] ?? "") : "";
//     };

//     // 🔹 Replace non-repeating placeholders
//     htmlContent = htmlContent.replace(
//       /<!--\s*\{([A-Z0-9_]+)\}\s*\.\s*\[\s*([^\]]+?)\s*\]\s*-->/gi,
//       (m, tag, field) => {
//         const val = getValue(dataMap[tag], field);
//         console.debug(`🔸 ${tag}.${field} -> "${val}"`);
//         return val ?? "";
//       }
//     );

//     // 🔹 Replace {@InvoiceCopyName}
//     const copyList = printData?.Table || [];
//     const copyObj =
//       (Array.isArray(copyList) &&
//         (copyList.find((x) => x.Default === 1) || copyList[0])) ||
//       {};
//     const copyName = copyObj?.CopyName || "Original";
//     htmlContent = htmlContent.replace(/{@InvoiceCopyName}/g, copyName);
//     console.log("📑 Invoice copy name used:", copyName);

//     // 🔹 Find RepeatSection - try multiple patterns
//     const patterns = [
//       /<\s*RepeatSection[^>]*>([\s\S]*?)<\/\s*RepeatSection\s*>/i,
//       /<RepeatSection>([\s\S]*?)<\/RepeatSection>/i,
//       /RepeatSection>([\s\S]*?)<\/RepeatSection/i,
//       /RepeatSection([\s\S]*?)<\/RepeatSection/i,
//     ];

//     let match = null;
//     let usedPattern = null;

//     for (const pattern of patterns) {
//       match = htmlContent.match(pattern);
//       if (match) {
//         usedPattern = pattern;
//         break;
//       }
//     }

//     console.log("🔍 RepeatSection search results:");
//     console.log("🔍 Pattern tried:", patterns.map((p) => p.toString()));
//     console.log("🔍 Match found?", !!match);
//     console.log("🔍 Used pattern:", usedPattern?.toString());
//     console.log("🔍 Match details:", match);

//     if (match) {
//       let rowTemplate = match[1].replace(/\u00A0/g, " ");
//       const items = (printData?.Table5 || []).filter((r) => {
//         const p = r?.Partno || r?.PARTNO || r?.partNo;
//         return p && String(p).trim() !== "";
//       });

//       console.log("📦 Filtered valid items:", items.length);

//   let rowsHtml = "";
// const totalRows = 10; // ✅ Minimum total rows per page (including empty)

// // ✅ Render actual data rows
// items.forEach((item, idx) => {
//   const slNo = item?.SlNo || idx + 1;
//   const partNo = item?.Partno || "";
//   const desc = item?.PartDesc || "";
//   const hsn = item?.HSNCode || "";
//   const uom = item?.UOM || "";
//   const qty = item?.Qty || "";
//   const rate = item?.Rate || "";
//   const amount = item?.Amount || "";

//   rowsHtml += `
//     <tr height=20 style='mso-height-source:userset;height:15.0pt'>
//       <td height=20 class="style4"
//         style='border-top-style:none;border-top-color:inherit;border-top-width:medium;'>${slNo}</td>
//       <td colspan=3 class="xl781326"
//         style='border-right:.5pt solid black;border-left:none;width:100pt;'>${partNo}</td>
//       <td colspan=7 class="xl781326"
//         style='border-right:.5pt solid black;border-left:none;width:134pt;'>${desc}</td>
//       <td colspan=2 class="xl1051326"
//         style='border-right:.5pt solid black;border-left:none;width:50pt;'>${hsn}</td>
//       <td class="style5" style='border-left:none;'>${uom}</td>
//       <td colspan=3 class="xl1561326"
//         style='border-right:.5pt solid black;border-left:none;'>${qty}</td>
//       <td colspan=2 class="xl1561326"
//         style='border-right:.5pt solid black;border-left:none;'>${rate}</td>
//       <td colspan=2 class="style6"
//         style='border-right:.5pt solid black;border-left-style:none;border-left-color:inherit;border-left-width:medium;'>${amount}</td>
//     </tr>
//   `;
// });

// // ✅ Add empty rows if less than 10
// if (items.length < totalRows) {
//   const emptyCount = totalRows - items.length;
//   for (let i = 0; i < emptyCount; i++) {
//     rowsHtml += `
//       <tr height=20 style='mso-height-source:userset;height:15.0pt'>
//         <td height=20 class="style4"
//           style='border-top-style:none;border-top-color:inherit;border-top-width:medium;'>&nbsp;</td>
//         <td colspan=3 class="xl781326"
//           style='border-right:.5pt solid black;border-left:none;width:100pt;'>&nbsp;</td>
//         <td colspan=7 class="xl781326"
//           style='border-right:.5pt solid black;border-left:none;width:134pt;'>&nbsp;</td>
//         <td colspan=2 class="xl1051326"
//           style='border-right:.5pt solid black;border-left:none;width:50pt;'>&nbsp;</td>
//         <td class="style5" style='border-left:none;'>&nbsp;</td>
//         <td colspan=3 class="xl1561326"
//           style='border-right:.5pt solid black;border-left:none;'>&nbsp;</td>
//         <td colspan=2 class="xl1561326"
//           style='border-right:.5pt solid black;border-left:none;'>&nbsp;</td>
//         <td colspan=2 class="style6"
//           style='border-right:.5pt solid black;border-left-style:none;border-left-color:inherit;border-left-width:medium;'>&nbsp;</td>
//       </tr>
//     `;
//   }
// }
//       if (rowsHtml.trim()) {
//         htmlContent = htmlContent.replace(usedPattern, rowsHtml);
//         console.log("✅ RepeatSection replaced with item rows.");
//       } else {
//         console.warn("⚠️ No valid rows found — RepeatSection removed.");
//         htmlContent = htmlContent.replace(usedPattern, "");
//       }
//     } else {
//       console.warn("⚠️ RepeatSection not found in HTML template!");
//     }

//     // 🔹 Cleanup any leftover placeholders
//     htmlContent = htmlContent.replace(
//       /<!--\s*\{[A-Z0-9_]+\}\s*\.\s*\[[^\]]+\]\s*-->/gi,
//       ""
//     );

//     console.log("📄 Final HTML length:", htmlContent.length);
//     console.groupEnd();
//     return htmlContent;
//   } catch (err) {
//     console.error("❌ populatePrintTemplate error:", err);
//     console.groupEnd();

//     // ✅ Force visible text in Excel-exported HTML
//     htmlContent = htmlContent.replace(
//       /<\/head>/i,
//       `<style>
//          body, table, tr, td, th, p, span, font {
//            color: black !important;
//            visibility: visible !important;
//            font-family: Arial, sans-serif !important;
//            font-size: 10pt !important;
//          }
//          td {
//            mso-number-format: "\\@"; /* prevent Excel-format hiding */
//          }
//        </style></head>`
//     );

//     return htmlContent;
//   }
// };



//   return (
//     <div className="movement-dashboard">
//       <style>{`
//         .movement-dashboard {
//           background: #f9fafb;
//           padding: 24px;
//           font-family: "Inter", sans-serif;
//           min-height: 100vh;
//         }

//         .header-section h2 {
//           text-align: center;
//           font-size: 1.4rem;
//           font-weight: 500;
//           color: #1f2937;
//         }

//         // .btn-primary {
//         //   display: flex;
//         //   align-items: center;
//         //   gap: 8px;
//         //   background: linear-gradient(90deg, #2563eb, #1d4ed8);
//         //   color: white;
//         //   border: none;
//         //   padding: 10px 18px;
//         //   border-radius: 8px;
//         //   font-weight: 500;
//         //   cursor: pointer;
//         //   transition: background 0.3s ease;
//         // }
//         // .btn-primary:hover {
//         //   background: linear-gradient(90deg, #1d4ed8, #1e40af);
//         // }

//         .btn-primary {
//   display: flex;
//   align-items: center;
//   gap: 4px; /* smaller space between icon and text */
//   background: #2563eb;
//   color: #fff;
//   border: none;
//   border-radius: 5px;
//   padding: 5px 10px; /* smaller padding */
//   font-size: 13px; /* reduced font size */
//   cursor: pointer;
// }

// .btn-primary:hover {
//   background: #1d4ed8;
// }


//         .btn-edit {
//           background: #1b6cc9ff;
//           color: white;
//           border: none;
//           padding: 6px 12px;
//           border-radius: 6px;
//           font-size: 0.85rem;
//           display: flex;
//           align-items: center;
//           gap: 5px;
//           cursor: pointer;
//           transition: 0.3s ease;
//         }
//         .btn-edit:hover {
//           background: #1b6cc9ff;
//         }

//         .btn-print {
//           background: #10b981;
//           color: white;
//           border: none;
//           padding: 6px 12px;
//           border-radius: 6px;
//           font-size: 0.85rem;
//           display: flex;
//           align-items: center;
//           gap: 5px;
//           cursor: pointer;
//           transition: 0.3s ease;
//         }
//         .btn-print:hover {
//           background: #059669;
//         }

//         .controls {
//           display: flex;
//           justify-content: space-between;
//           align-items: center;
//           flex-wrap: wrap;
//           margin-top: 16px;
//           gap: 10px;
//         }

//         .controls input {
//           flex: 1;
//           min-width: 180px;
//           padding: 10px;
//           border: 1px solid #d1d5db;
//           border-radius: 8px;
//           font-size: 0.95rem;
//           outline: none;
//           transition: 0.2s;
//         }

//         .controls input:focus {
//           border-color: #2563eb;
//           box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
//         }

//         .view-toggle {
//           display: flex;
//           gap: 8px;
//         }

//         .view-toggle button {
//           padding: 8px 16px;
//           border-radius: 6px;
//           border: 1px solid #d1d5db;
//           background: white;
//           color: #374151;
//           font-weight: 500;
//           cursor: pointer;
//           transition: all 0.2s ease;
//         }

//         .view-toggle button:hover {
//           background: #f3f4f6;
//         }

//         .view-toggle .active {
//           background: #2563eb;
//           color: white;
//           border-color: #1d4ed8;
//         }

//         .table-container {
//           overflow-x: auto;
//           margin-top: 20px;
//           background: white;
//           border-radius: 10px;
//           box-shadow: 0 1px 3px rgba(59, 51, 51, 0.08);
//         }

//         table {
//           width: 100%;
//           border-collapse: collapse;
//           font-size: 0.9rem;
//         }

//         thead {
//           background: #383131ff;
//           color: white;
//         }

//         th, td {
//           text-align: left;
//           padding: 10px 14px;
//           border-bottom: 1px solid #e5e7eb;
//         }

//         tbody tr:hover {
//           background: #f9fafb;
//         }

//         .card-container {
//           display: grid;
//           grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
//           gap: 16px;
//           margin-top: 20px;
//         }

//         .movement-card {
//           background: white;
//           border: 1px solid #e5e7eb;
//           border-radius: 12px;
//           padding: 16px;
//           box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
//           transition: transform 0.2s ease, box-shadow 0.2s ease;
//         }

//         .movement-card:hover {
//           transform: translateY(-3px);
//           box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
//         }

//         .movement-card h3 {
//           font-size: 1.1rem;
//           font-weight: 600;
//           color: #1d4ed8;
//           margin-bottom: 8px;
//         }

//         .movement-card p {
//           font-size: 0.9rem;
//           color: #374151;
//           margin: 4px 0;
//         }

//         .movement-card strong {
//           color: #111827;
//         }

//         .loader {
//           text-align: center;
//           padding: 40px;
//           font-size: 1rem;
//           color: #374151;
//         }
//           /* Styles copied from TicketSummary.js for logo/company name header */
//         .header-controls {
//           display: flex;
//           justify-content: center;
//           align-items: center;
//           margin-bottom: 20px;
//           background: #fff;
//           padding: 4px 20px;
//           border-radius: 10px;
//           box-shadow: 0 2px 6px rgba(0,0,0,0.1);
//           position: relative; /* To center the title */
//         }
        
//         .logo-and-name {
//           display: flex;
//           align-items: center;
//           position: absolute; /* Position absolute to move it to the left */
//           left: 20px;
//           top: 50%;
//           transform: translateY(-50%);
//         }
        
//         .logo-and-name img {
//           width: 60px;
//           height: 60px;
//           border-radius: 50%;
//           background-color: white;
//           box-shadow: 0 0 4px rgba(0,0,0,0.2);
//           padding: 5px;
//           margin-right: 1px;
//           object-fit: contain;
//         }

//         .logo-and-name h1 {
//           font-size: 22px;
//           font-weight: bold;
//           color: #333;
//           margin: 0;
//           text-align: left;
//         }
          
//       `}</style>

//       <div className="header-section">
//         <div className="flex flex-wrap items-center justify-between gap-3">
//          <div className="header-controls">
//         <div className="logo-and-name">
//           <img
//             src={logo}
//             alt="Company Logo"
//           />
//           <h1>{companyName}</h1>
//         </div>
//         <h2 className="page-title">Asset Movement</h2>
//         </div>
//           <button
//             className="btn-primary"
//             onClick={() => navigate("/movement/new")}
//           >
//             <PlusCircle className="w-4 h-4" /> New
//           </button>
//         </div>

//         <div className="controls">
//           <input
//             type="text"
//             placeholder="🔍 Search movement details..."
//             value={filter}
//             onChange={(e) => setFilter(e.target.value)}
//           />

//  <ExcelExport data={movementList} fileName="AssetMovementList" />
 
//           <div className="view-icons">
//             <button
//               onClick={() => setViewMode("card")}
//               className={`icon-btn ${viewMode === "card" ? "active" : ""}`}
//               title="Card View"
//             >
//               <svg
//                 xmlns="http://www.w3.org/2000/svg"
//                 className="icon-svg"
//                 fill="currentColor"
//                 viewBox="0 0 24 24"
//               >
//                 <path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
//               </svg>
//             </button>

//             <button
//               onClick={() => setViewMode("table")}
//               className={`icon-btn ${viewMode === "table" ? "active" : ""}`}
//               title="Table View"
//             >
//               <svg
//                 xmlns="http://www.w3.org/2000/svg"
//                 className="icon-svg"
//                 fill="currentColor"
//                 viewBox="0 0 24 24"
//               >
//                 <path d="M4 6h16v2H4V6zm0 4h16v2H4v-2zm0 4h16v2H4v-2zm0 4h16v2H4v-2z" />
//               </svg>
//             </button>
//           </div>
//         </div>
//       </div>

//       {loading ? (
//         <div className="loader">Loading movement data...</div>
//       ) : filteredData.length === 0 ? (
//         <div className="loader">No asset movements found.</div>
//       ) : viewMode === "table" ? (
//         <div className="table-container">
//           <table>
//             <thead>
//               <tr>
//                 <th>Doc No</th>
//                 <th>Asset Name</th>
//                 <th>Movement Date</th>
//                 <th>Reason</th>
//                 <th>FromLocation</th>
//                 <th>ToLocation</th>
//                 <th>Returnable</th>
//                 <th>Expected Return</th>
//                 <th>Auth Order</th>
//                 <th>Transport</th>
//                 <th>Condition</th>
//                 <th>Print</th>
//                 <th>Action</th>
//               </tr>
//             </thead>
//             <tbody>
//               {filteredData.map((move) => (
//                 <tr key={move.MovementID}>
//                   <td>{move.MovementDocNo}</td>
//                   <td>{move.AssetName}</td>
//                   <td>{move.MovementDate?.split("T")[0]}</td>
//                   <td>{move.MovementReason}</td>
//                   <td>{move.FromLocation}</td>
//                   <td>{move.ToLocation}</td>
//                   <td>{move.IsReturnable ? "Yes" : "No"}</td>
//                   <td>{move.ExpectedReturnDate?.split("T")[0]}</td>
//                   <td>{move.AuthOrderNo}</td>
//                   <td>{move.TransportMode}</td>
//                   <td>{move.ConditionNotes}</td>
//                   <td>
//                     <button
//                       className="btn-print"
//                      onClick={() => handlePrint(move.GIPNRId || move.GipId || move.GIPID)}
//                     >
//                       🖨️ Print
//                     </button>
//                   </td>
//                   <td>
//                     <button
//                       className="btn-edit"
//                       onClick={() => handleEdit(move.MovementID)}
//                     >
//                       ✏️ Edit
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       ) : (
//         <div className="card-container">
//           {filteredData.map((move) => (
//             <div key={move.MovementID} className="movement-card">
//               <h3>{move.MovementDocNo}</h3>
//               <p><strong>Asset:</strong> {move.AssetName}</p>
//               <p><strong>Date:</strong> {move.MovementDate?.split("T")[0]}</p>
//               <p><strong>Reason:</strong> {move.MovementReason}</p>
//               <p><strong>From:</strong> {move.FromLocation}</p>
//               <p><strong>To:</strong> {move.ToLocation}</p>
//               <p><strong>Transport:</strong> {move.TransportMode}</p>
//               <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
//                 <button
//                   className="btn-print"
//                  onClick={() => handlePrint(move.GIPNRId || move.GipId || move.GIPID)}
//                 >
//                   🖨️ Print
//                 </button>
//                 <button
//                   className="btn-edit"
//                   onClick={() => handleEdit(move.MovementID)}
//                 >
//                   ✏️ Edit
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// }
